import React, { useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// MUI Components
import {
  Box,
  Typography,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';

// MUI Icons
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';
import CloudIcon from '@mui/icons-material/Cloud';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

// Images
import scaleLogo from '../assets/scale.png';
import scaleLogoMain from '../assets/scale-main.png';

// Styled Components
import { styled } from '@mui/system';
import { LINK_COLOR, GRADIENT_START, GRADIENT_END, SECONDARY_MAIN, PRIMARY_MAIN } from '../utilities/constants';

const StyledLink = styled(Link)(({ theme }) => ({
  color: LINK_COLOR,
  textDecoration: 'underline',
  component: 'a',
  '&:hover': {
    color: LINK_COLOR,
    opacity: 0.8,
  },
}));

const StyledEmailLink = styled(Link)(({ theme }) => ({
  color: LINK_COLOR,
  textDecoration: 'none',
  component: 'a',
  '&:hover': {
    color: LINK_COLOR,
    textDecoration: 'underline',
    opacity: 0.8,
  },
}));

const LandingPage = () => {
  // Check if we're in dev mode first
  const isPlaceholderConfig = process.env.REACT_APP_USER_POOL_ID && 
                              process.env.REACT_APP_USER_POOL_ID.includes('PLACEHOLDER');
  const isDevMode = process.env.REACT_APP_DEV_MODE === 'true' || isPlaceholderConfig;
  
  // useAuth must be called unconditionally, but we'll handle errors gracefully
  const auth = useAuth();
  const navigate = useNavigate();
  const [heroLoading, setHeroLoading] = useState(false);
  const [ctaLoading, setCtaLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    // Skip auth checks in dev mode
    if (isDevMode) return;

    // Handle auth errors gracefully
    if (auth.error) {
      console.warn('Auth error in LandingPage (dev mode may be active):', auth.error);
      return;
    }

    // Removed automatic redirect - allow authenticated users to view landing page
    // if (auth.isLoading) return;
    // if (auth.isAuthenticated) {
    //   navigate('/app', { replace: true });
    // }
  }, [auth, isDevMode, navigate]);

  const handleSignIn = (source = 'hero') => {
    const setLoading = source === 'hero' ? setHeroLoading : setCtaLoading;

    // If already authenticated, navigate directly to app
    if (!isDevMode && auth && auth.isAuthenticated) {
      setLoading(true);
      setTimeout(() => {
        navigate('/app', { replace: true });
        setLoading(false);
      }, 300);
      return;
    }

    if (isDevMode) {
      // In dev mode, navigate directly to /app without authentication
      setLoading(true);
      setTimeout(() => {
        navigate('/app', { replace: true });
        setLoading(false);
      }, 500);
      return;
    }

    if (!auth) {
      alert('Authentication not available. Please check your configuration.');
      return;
    }

    setLoading(true);

    // // Minimal debug to inspect OIDC settings at the moment we start login
    // try {
    //   console.log('Starting OIDC signinRedirect with settings:', {
    //     authority: auth.settings?.authority,
    //     client_id: auth.settings?.client_id,
    //     redirect_uri: auth.settings?.redirect_uri,
    //     post_logout_redirect_uri: auth.settings?.post_logout_redirect_uri,
    //     currentUrl: typeof window !== 'undefined' ? window.location.href : 'N/A',
    //     currentOrigin: typeof window !== 'undefined' ? window.location.origin : 'N/A',
    //   });
    // } catch (e) {
    //   console.warn('Failed to log OIDC signin settings:', e);
    // }

    // Introduce a 1-second delay before redirecting
    setTimeout(() => {
      try {
        auth.signinRedirect();
        // No need to reset loading here as redirect will occur
      } catch (error) {
        console.error('Sign-in error:', error);
        setLoading(false);
        alert('Unable to sign in. Please check your configuration. See browser console for details.');
      }
    }, 1000);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Skip loading check in dev mode or if auth has errors
  if (!isDevMode && !auth.error && auth.isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={50} thickness={5} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Navigation Bar */}
      <Box
        sx={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #e0e0e0',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: { xs: 2, md: 6 },
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        }}
      >
        <img
          src={scaleLogoMain}
          alt="ScaleCapacity logo"
          style={{
            height: '40px',
            width: 'auto',
          }}
        />
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${GRADIENT_START} 0%, ${GRADIENT_END} 100%)`,
          color: '#fff',
          py: { xs: 6, md: 9 },
          px: { xs: 3, md: 6 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: 0.1,
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        />
        
        <Box
          sx={{
            maxWidth: '1200px',
            margin: '0 auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '3.5rem' },
              mb: 3,
              lineHeight: 1.2,
              textAlign: 'center',
            }}
          >
            Transform PDFs into Accessible Documents
            <Box component="span" sx={{ display: 'block', color: SECONDARY_MAIN, mt: 1 }}>
              WCAG 2.1 AA Compliant in Minutes
            </Box>
          </Typography>
          
          {/* Deadline Alert */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              mt: 3,
              mb: 3,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                px: 3,
                py: 1.25,
                borderRadius: '8px',
                backgroundColor: 'rgba(255, 251, 235, 0.95)',
                border: '1px solid rgba(253, 230, 138, 0.8)',
                borderLeft: '3px solid rgba(217, 119, 6, 0.75)',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                maxWidth: '700px',
                width: '100%',
              }}
            >
              <ErrorOutlineIcon sx={{ color: '#D97706', fontSize: 20, flexShrink: 0, opacity: 0.85 }} />
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: '#78350F',
                    lineHeight: 1.3,
                    fontSize: '0.9rem',
                  }}
                >
                  New regulations: Compliance deadline April 2026
                </Typography>
              </Box>
            </Box>
          </Box>

            <Typography
            variant="h6"
            component="p"
              sx={{
              fontSize: { xs: '1rem', md: '1.25rem' },
                mb: 4,
                textAlign: 'center',
              maxWidth: '700px',
              margin: '0 auto 2rem',
              opacity: 0.95,
              lineHeight: 1.6,
              }}
            >
            Enterprise-grade PDF accessibility remediation powered by AWS and Adobe AI. 
            Deliver compliant documents faster with our secure, scalable SaaS platform trusted by leading organizations.
            </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <LoadingButton
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIosIcon />}
              onClick={() => handleSignIn('hero')}
              loading={heroLoading}
              loadingIndicator={<CircularProgress size={24} sx={{ color: '#FFFFFF' }} />}
              sx={{
                backgroundColor: SECONDARY_MAIN,
                color: GRADIENT_START,
                fontWeight: 700,
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                textTransform: 'none',
                boxShadow: '0 4px 14px rgba(84, 251, 245, 0.3)',
                '&:hover': {
                  backgroundColor: '#45e8e0',
                  boxShadow: '0 6px 20px rgba(84, 251, 245, 0.4)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Get Demo
            </LoadingButton>
            
            <LoadingButton
              variant="outlined"
              size="large"
              onClick={(e) => {
                e.preventDefault();
                handleOpenDialog();
              }}
              sx={{
                borderColor: 'rgba(255, 255, 255, 0.5)',
                color: '#fff',
                fontWeight: 600,
                fontSize: '1.1rem',
                px: 4,
                py: 1.5,
                borderRadius: '8px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#fff',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Learn More
            </LoadingButton>
          </Box>
        </Box>
      </Box>

      {/* Cost estimator – subtle block below hero */}
      <Box
        sx={{
          py: { xs: 2.5, md: 3 },
          px: { xs: 2, md: 4 },
          backgroundColor: '#fafafa',
          borderBottom: '1px solid #eee',
        }}
      >
        <Box
          component={RouterLink}
          to="/AccessWCAGCostEstimator"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            maxWidth: '420px',
            margin: '0 auto',
            py: 3,
            px: 4,
            borderRadius: '12px',
            background: 'linear-gradient(to right, #7c3aed, #0891b2)',
            border: '2px solid rgba(255, 255, 255, 0.35)',
            textDecoration: 'none',
            color: '#fff',
            fontSize: '1.1rem',
            fontWeight: 700,
            transition: 'all 0.25s ease',
            boxShadow: '0 4px 14px rgba(124, 58, 237, 0.45), 0 2px 6px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(124, 58, 237, 0.5), 0 4px 12px rgba(0, 0, 0, 0.12)',
              borderColor: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
            Calculate your investment
          </Typography>
          <Typography component="span" sx={{ color: 'inherit', opacity: 0.95 }}>→</Typography>
        </Box>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 5, md: 7 },
          px: { xs: 3, md: 6 },
          backgroundColor: '#fff',
        }}
      >
        <Box sx={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              mb: 1,
              color: '#1a1a1a',
            }}
          >
            Why Choose Our Platform?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              textAlign: 'center',
              mb: 5,
              color: '#666',
              fontSize: '1.1rem',
              maxWidth: '600px',
              margin: '0 auto',
            }}
          >
            Enterprise-grade PDF accessibility remediation powered by AWS cloud infrastructure and Adobe AI technology
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              gap: 4,
            mt: 3,
            }}
          >
            {[
              {
                icon: <SpeedIcon sx={{ fontSize: 40 }} />,
                title: 'Lightning-Fast Processing',
                description: 'Get your remediated PDFs in minutes, not days. Automated AWS-powered workflows deliver rapid results.',
              },
              {
                icon: <AccessibilityIcon sx={{ fontSize: 40 }} />,
                title: 'WCAG 2.1 AA Compliant',
                description: 'Guaranteed full compliance with accessibility standards. Screen reader friendly and accessible to all users.',
              },
              {
                icon: <CloudIcon sx={{ fontSize: 40 }} />,
                title: 'Powered by AWS',
                description: 'Enterprise-grade cloud infrastructure scales effortlessly. Built on AWS for reliability, security, and performance.',
              },
              {
                icon: <SecurityIcon sx={{ fontSize: 40 }} />,
                title: 'Enterprise Security',
                description: 'Bank-level security with industry-standard encryption. Your documents are protected with enterprise-grade safeguards.',
              },
            ].map((feature, index) => (
              <Box
                key={index}
                sx={{
                  p: 3,
                  borderRadius: '12px',
                  border: '1px solid #e0e0e0',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    transform: 'translateY(-4px)',
                    borderColor: PRIMARY_MAIN,
                  },
                }}
              >
                <Box
                  sx={{
                    color: PRIMARY_MAIN,
                    mb: 2,
                  }}
                >
                  {feature.icon}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: '#1a1a1a',
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    lineHeight: 1.6,
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* How It Works Section */}
      <Box
        sx={{
          py: { xs: 5, md: 7 },
          px: { xs: 3, md: 6 },
          backgroundColor: '#f8f9fa',
        }}
      >
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              mb: 5,
              color: '#1a1a1a',
            }}
          >
            How It Works
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 4,
            }}
          >
            {[
              {
                step: '1',
                title: 'Get Started & Upload',
                description: 'Access your account and upload your PDF document. Our AWS-powered platform handles files up to 25MB seamlessly.',
              },
              {
                step: '2',
                title: 'AI-Powered Remediation',
                description: 'Advanced Adobe AI and AWS automation fix accessibility issues including tags, reading order, and alt text in real-time.',
              },
              {
                step: '3',
                title: 'Download & Deploy',
                description: 'Instantly receive your WCAG 2.1 AA compliant PDF and comprehensive accessibility report, ready for immediate use.',
              },
            ].map((item, index) => (
              <Box
                key={index}
                sx={{
                  textAlign: 'center',
                  p: 3,
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: PRIMARY_MAIN,
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.5rem',
                    fontWeight: 700,
                    margin: '0 auto 1.5rem',
                  }}
                >
                  {item.step}
                </Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 1,
                    color: '#1a1a1a',
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#666',
                    lineHeight: 1.6,
                  }}
                >
                  {item.description}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 5, md: 7 },
          px: { xs: 3, md: 6 },
          background: `linear-gradient(135deg, ${GRADIENT_START} 0%, ${GRADIENT_END} 100%)`,
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <Box sx={{ maxWidth: '800px', margin: '0 auto' }}>
          <Typography
            variant="h3"
            component="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '2rem', md: '2.5rem' },
            }}
          >
            Ready to Transform Your PDFs?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 4,
              opacity: 0.95,
              fontSize: { xs: '1rem', md: '1.2rem' },
            }}
          >
            Join leading organizations that trust our AWS-powered platform for enterprise accessibility needs
          </Typography>
          <LoadingButton
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIosIcon />}
            onClick={() => handleSignIn('cta')}
            loading={ctaLoading}
            loadingIndicator={<CircularProgress size={24} sx={{ color: '#FFFFFF' }} />}
            sx={{
              backgroundColor: SECONDARY_MAIN,
              color: GRADIENT_START,
              fontWeight: 700,
              fontSize: '1.1rem',
              px: 5,
              py: 1.5,
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: '0 4px 14px rgba(84, 251, 245, 0.3)',
              '&:hover': {
                backgroundColor: '#45e8e0',
                boxShadow: '0 6px 20px rgba(84, 251, 245, 0.4)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            Get Demo
          </LoadingButton>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 3,
          px: { xs: 3, md: 6 },
          backgroundColor: '#1a1a1a',
          color: '#fff',
        }}
      >
        <Box sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Box
          sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 3,
            }}
          >
            <Box>
              <img
                src={scaleLogo}
                alt="ScaleCapacity logo"
                style={{
                  height: '32px',
                  width: 'auto',
                  filter: 'brightness(0) invert(1)',
                }}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: '#999', mb: 1 }}>
                Need help? Contact our support team
        </Typography>
              <StyledEmailLink
                href="mailto:accesswcag-support@scalecapacity.com"
                sx={{ color: SECONDARY_MAIN, fontSize: '0.95rem' }}
              >
                accesswcag-support@scalecapacity.com
              </StyledEmailLink>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: '#999' }}>
                Powered by{' '}
          <StyledLink
            href="https://www.scalecapacity.com"
            target="_blank"
            rel="noopener"
                  sx={{ color: SECONDARY_MAIN }}
          >
            ScaleCapacity
                </StyledLink>
        </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 
        Dialog (modal) for remediation process
      */}
      <Dialog
  open={openDialog}
  onClose={handleCloseDialog}
  aria-labelledby="remediation-dialog-title"
>
  <DialogTitle
    id="remediation-dialog-title"
    sx={{ pr: 4, position: 'relative' }}
  >
    <strong>Remediation Process</strong>
    <IconButton
      aria-label="close"
      onClick={handleCloseDialog}
      sx={{
        position: 'absolute',
        right: 8,
        top: 8,
        color: (theme) => theme.palette.grey[500],
      }}
    >
      <CloseIcon />
    </IconButton>
  </DialogTitle>

  <DialogContent dividers>
    <Typography variant="body1" component="p" paragraph>
      Our streamlined PDF remediation process delivers WCAG 2.1 AA compliant documents in three simple steps:
    </Typography>

    <Typography variant="body2" component="p" paragraph>
      <strong>1. Upload Your PDF:</strong> Access your account and upload your PDF document. 
      Our AWS-powered platform securely processes files up to 25MB with enterprise-grade infrastructure.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      <strong>2. AI-Powered Remediation:</strong> Advanced Adobe AI and AWS automation work together 
      to fix accessibility issues including document structure tags, reading order, alt text, and metadata 
      to ensure full WCAG 2.1 AA compliance.
    </Typography>
    <Typography variant="body2" component="p" paragraph>
      <strong>3. Download & Deploy:</strong> Receive your fully remediated PDF and comprehensive 
      accessibility report within minutes. Your documents are ready for immediate distribution and use.
    </Typography>

    <Box sx={{ mt: 3, p: 2, backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
      <Typography variant="body2" component="p" sx={{ mb: 1, fontWeight: 600 }}>
        Enterprise Support Available
    </Typography>
      <Typography variant="body2" component="p" sx={{ color: '#666' }}>
        Need assistance or have questions? Our dedicated support team is here to help. 
        Contact us at{' '}
        <StyledEmailLink href="mailto:accesswcag-support@scalecapacity.com">
          accesswcag-support@scalecapacity.com
        </StyledEmailLink>
    </Typography>
    </Box>
  </DialogContent>
</Dialog>
    </Box>
  );
};

export default LandingPage;
